import { gql } from "@apollo/client";

export const PROFILE_DOCUMENT_REFERENCE_FIELDS_FRAGMENT = gql`
  fragment ProfileDocumentReferenceFields on BackofficePersonProfileDocument {
    id
    name
    filename
    url
    category
    createdAt
    updatedAt
  }
`;

export const PERSON_FRAGMENT = gql`
  fragment PersonFragment on BackofficePerson {
    id
    firstName
    middleName
    lastName
    displayName
    informalName
    preferredLastName
    suffixName
    profilePhoto
    pgaId
  }
`;

export const SALESFORCE_PERSON_FRAGMENT = gql`
  fragment SalesforcePersonFragment on BackofficePerson {
    id
    firstName
    lastName
    profilePhoto
    pgaId
    interactions(search: $search, first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        createdAt
        categories
        owner {
          name
          email
        }
        showAiSummary
        messages {
          content
          createdAt
          isPrivate
          fromOwner
          haveAccess
        }
        lastMessage {
          content
          createdAt
          isPrivate
          fromOwner
          haveAccess
        }
        jobPost {
          id
          name
          category {
            name
          }
        }
        lastModifiedDate
        interactionDate
        messagesCount
        mode
        participants {
          firstName
          lastName
          profilePhoto
          pgaId
          currentStatus
          classification
          employments {
            isPrimary
            facility {
              name
            }
          }
          primarySectionAffiliation {
            section {
              name
            }
          }
        }
        summary
        showAiSummary
        relatedTo
        openTasksCount
        twilioFlexCall {
          transcriptSummaryStatus
        }
        _permissions
      }
    }
  }
`;

export const CRM_NOTE_FIELDS_FRAGMENT = gql`
  fragment CRMNoteFields on CRMNote {
    id
    title
    content
    displayTitle
    priority
    priorityExpirationDate
    visibility
    notableId
    notableType
    authorName
    createdAt
    updatedAt
    eventSlug
    author {
      id
      name
      email
    }
    openTasksCount
    references {
      __typename
      ... on BackofficePersonProfileDocument {
        ...ProfileDocumentReferenceFields
      }
    }
  }
  ${PROFILE_DOCUMENT_REFERENCE_FIELDS_FRAGMENT}
`;

export const MEMBERSHIP_BAND_FRAGMENT = gql`
  fragment MembershipBandFragment on MembershipBand {
    id
    createdReason
    terminatedReason
    sectionAffiliation {
      id
      primary
      section {
        id
        name
        legacyId
      }
    }
    section {
      id
      name
      legacyId
    }
    programType {
      id
      name
    }
    expiresOn
    startedOn
    terminatesOn
    classification {
      id
      name
      description
    }
    notes {
      ...CRMNoteFields
    }
  }
  ${CRM_NOTE_FIELDS_FRAGMENT}
`;

export const PHONE_NUMBER_FRAGMENT = gql`
  fragment PhoneNumberFragment on CRMPhoneNumber {
    id
    phoneNumber
    phoneExtension
    phoneType
    phoneCountry
    phoneFormatted
  }
`;

export const ADDRESS_FRAGMENT = gql`
  fragment AddressFragment on CRMAddress {
    id
    address1
    address2
    city
    state
    postalCode
    countryCode
    addressType
    isVerified
  }
`;

export const PERSON_DETAILS_FRAGMENT = gql`
  fragment PersonDetailsFragment on BackofficePerson {
    id
    firstName
    middleName
    lastName
    suffixName
    displayName
    gender
    race
    pgaId
    email
    profilePhoto
    informalName
    preferredLastName
    spouseName
    directoryOptOut
    militaryService {
      id
      status
    }
    primarySection {
      id
      name
    }
    primarySectionAffiliation {
      id
      primary
      startAt
      endAt
      chapterAffiliations {
        id
        attributeCode
        chapter {
          id
          code
          name
        }
        createdAt
        createdBy
      }
      chapterAffiliationDescription
      section {
        id
        name
      }
    }
    sectionAffiliations {
      id
      primary
      startAt
      endAt
      chapterAffiliations {
        id
        attributeCode
        chapter {
          id
          code
          name
        }
        createdAt
        createdBy
      }
      chapterAffiliationDescription
      section {
        id
        name
      }
    }
    activeSectionAffiliations {
      id
      primary
      startAt
      endAt
      chapterAffiliations {
        id
        attributeCode
        chapter {
          id
          code
          name
        }
        createdAt
        createdBy
      }
      chapterAffiliationDescription
      section {
        id
        name
      }
    }
    phoneNumbers {
      ...PhoneNumberFragment
    }
    contactAddressId
    addresses {
      ...AddressFragment
    }
    employments {
      id
      endedOn
      startedOn
      isPrimary
      title
      formattedTitle
      jobClassification
      facility {
        id
        name
        addresses {
          id
          address1
          address2
          city
          state
          postalCode
          countryCode
          isVerified
          addressType
        }
        phone {
          id
          phoneFormatted
        }
        section {
          id
          name
        }
      }
    }
    nameChangeDocument {
      id
      name
      category
      filename
      url
      createdAt
      updatedAt
    }
  }
  ${PHONE_NUMBER_FRAGMENT}
  ${ADDRESS_FRAGMENT}
`;

export const CCMS_SUSPENSION_FRAGMENT = gql`
  fragment CcmsSuspensionFragment on MembershipCcmsSuspension {
    id
    code
    startDate
    endDate
    reasonDescription
    membershipSuspensionId
  }
`;

export const CRM_TASK_FIELDS_FRAGMENT = gql`
  fragment CRMTaskFields on CRMTask {
    id
    name
    description
    stage
    visibility
    dueDate
    completedAt
    createdAt
    updatedAt
    author {
      id
      name
      email
      photo
    }
    assignedTo {
      id
      name
      email
      photo
    }
    teamTags {
      source
      name
    }
    assignedToDepartment
    authorDepartment
    references {
      __typename
      ... on CRMNote {
        id
        title
        displayTitle
        notableId
        notableType
        notableName
        personPgaId
        eventSlug
        universityLegacyId
        authorName
        updatedAt
        createdAt
      }
      ... on CRMInteraction {
        id
        relatedTo
        mode
        lastModifiedDate
        createdAt
        categories
        owner {
          name
        }
        participants {
          pgaId
          firstName
          lastName
        }
      }
      ... on BackofficeMembershipApplication {
        id
        applicationType
        status
        createdAt
        person {
          pgaId
          displayName
        }
      }
      ... on CommerceOrder {
        id
        description
        status
        dueDate
        balance
        updatedAt
        person {
          pgaId
          firstName
          lastName
          displayName
        }
      }
      ... on CommercePayment {
        id
        amountCents
        type
        status
        paidAt
        payer {
          id
          name
        }
      }
      ... on CRMPerson {
        id
        pgaId
        firstName
        lastName
        displayName
        profilePhoto
        email
      }
      ... on CRMEvent {
        id
        title
        slug
        status
        startsAt
        endsAt
      }
      ... on BackofficeCareerPlan {
        id
        title
        status
        updatedAt
        pgaId
      }
    }
    changelogs {
      id
      description
      timestamp
      modifiedBy
    }
  }
`;

export const DUES_RATE_FRAGMENT = gql`
  fragment DuesRateFragment on BackofficeDuesDuesRate {
    bypassValidation
    classification
    classificationDescription
    finalized
    id
    lookupKey
    memberGrade
    name
    notes
    previousPricing {
      price
      priceCents
      year
    }
    price
    priceCents
    productName
    updatedAt
  }
`;
